/**
 * UserInput.js  — Create Cost Sheet
 * Uses shared/designSystem.js + shared/LineItemsTable.jsx
 */
import React, { useState, useEffect, useContext } from 'react';
import { useAuth }          from './AuthProvider';
import { ItemsContext }     from './App';
import { useMsal }          from '@azure/msal-react';
import { getApiToken }      from './authUtils';
import UserSelect           from './helpers/UserSelect';
import ProductSelectorModal from './components/ProductSelectorModal';

import {
  COLORS, FONT, FIELD_SX, SELECT_SX,
  PageShell, PageHeader, SectionCard, SectionHeader, SectionBody,
  AggregateBar, PAYMENT_TERM_OPTIONS,
  formatCurrency,
  INTERNAL_CATEGORY,
} from './shared/DesignSystem';
import LineItemsTable from './shared/LineItemsTable';


import {
  Box, Button, Grid, Typography, Paper, Modal, Chip,
  TextField, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, LinearProgress,
  Autocomplete,
} from '@mui/material';
import { styled, alpha } from '@mui/system';

import AssignmentIcon         from '@mui/icons-material/Assignment';
import BusinessCenterIcon     from '@mui/icons-material/BusinessCenter';
import CalendarMonthIcon      from '@mui/icons-material/CalendarMonth';
import InventoryIcon          from '@mui/icons-material/Inventory';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon         from '@mui/icons-material/ExpandMore';
import TrendingUpIcon         from '@mui/icons-material/TrendingUp';
import ClearAllIcon           from '@mui/icons-material/ClearAll';
export { formatCurrency } from './shared/DesignSystem';


const urlconfig = require('./config/urlconfig');

// ─── LOCAL STYLED ────────────────────────────────────────────────────────────

const SampleBox = styled(Box)({
  marginTop: 20,
  padding: '16px 20px',
  borderRadius: 10,
  background: alpha(COLORS.amber, 0.06),
  border: `1px solid ${alpha(COLORS.amber, 0.22)}`,
});

const ConfirmPaper = styled(Paper)({
  position: 'absolute',
  top: '50%', left: '50%',
  transform: 'translate(-50%,-50%)',
  width: 480, maxWidth: '92vw',
  borderRadius: 16,
  boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
  overflow: 'hidden',
});

const SummaryKv = ({ label, value }) => (
  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
    <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: COLORS.textMuted, display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={700} sx={{ mt: 0.25, color: COLORS.textPrimary }}>{value ?? '—'}</Typography>
  </Box>
);

const SubmitBar = styled(Box)({
  position: 'sticky', bottom: 24,
  display: 'flex', justifyContent: 'flex-end', gap: 12,
  background: 'rgba(248,250,252,0.9)', backdropFilter: 'blur(8px)',
  borderRadius: 12, padding: '14px 20px',
  border: `1px solid ${COLORS.border}`,
  boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  zIndex: 100,
});

// ─── INITIAL STATE ───────────────────────────────────────────────────────────

const INITIAL_ITEM = {
  details: '', customDetails: '', qty: '', gst: '', pp: '', nlc: '', sp: '', nsp: '',
  totalPp: '', totalSp: '', margin: '', marginPercent: '',
  vendorName: '', vendorPaymentTerms: '',
  brand: null, category: null, subcategory: null,
  esin: '', ecomProductName: '',
};

// ─── COMPONENT ───────────────────────────────────────────────────────────────

const UserInput = () => {
  const { username, email, account } = useAuth();
  const { instance } = useMsal();

  const [formData, setFormData] = useState({
    date: '', deliveryDate: '', createdBy: '', userEmail: '',
    clientName: '', clientPaymentTerms: '', costsheetfor: '',
    salesOrderNo: '', salesPOC: '', scmspoc: '', costSheetRemarks: '',
    orderStatus: 'Pending',
    items: [{ ...INITIAL_ITEM }],
    transportationGST:'0', 
    transportationTotalPp: '0', transportationTotalSp: '0',
    customizationGST:'0', 
    customizationTotalPp: '0',  customizationTotalSp: '0',
    marketingGST:'0', 
    marketingTotalPp: '0',      marketingTotalSp: '0',
    totalPP: '0.00', totalSP: '0.00', totalMargin: '0.00', totalMarginPercent: '0.00',
    location: '', sampleStatus: 'Yet to receive from Vendor', internalCategory:'',
    legalClientName:'',
  });

  const [showCustomClientTerm, setShowCustomClientTerm] = useState(false);
  const [customClientTerm,     setCustomClientTerm]     = useState('');
  const [customVendorTerms,    setCustomVendorTerms]    = useState({});
  const [salesOrderExists,     setSalesOrderExists]     = useState(false);
  const [salesOrderError,      setSalesOrderError]      = useState('');
  const [qtyErrors,            setQtyErrors]            = useState({});
  const [brands,               setBrands]               = useState([]);
  const [categories,           setCategories]           = useState([]);
  const [categorySubcategoryMap, setCategorySubcategoryMap] = useState({});
  const [catalogLoading,       setCatalogLoading]       = useState(false);
  const [productModalOpen,     setProductModalOpen]     = useState(false);
  const [targetRowIndex,       setTargetRowIndex]       = useState(null);
  const [showConfirm,          setShowConfirm]          = useState(false);
  const [loading,              setLoading]              = useState(false);
  const [validated,            setValidated]            = useState(false);
  const [itemsExpanded,        setItemsExpanded]        = useState(true);
  const [customRows, setCustomRows] = useState([]);
  const [legalclients, setLegalClients] = useState([]);
  const { legalClients, legalVendors, refreshLegalClientsAndVendors } = useContext(ItemsContext);
  const [selectedLegalClient, setSelectedLegalClient] = useState(null);

  const isSample = formData.costsheetfor === 'Sample';
  const isInternal = formData.costsheetfor === 'internal';
  const isInventory = formData.costsheetfor === 'inventory';
  const isAutoSo = isSample || isInternal || isInventory;

  // ── TOTALS ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const itemsPP = formData.items.reduce((s, i) => s + (parseFloat(i.totalPp) || 0), 0);
    const itemsSP = formData.items.reduce((s, i) => s + (parseFloat(i.totalSp) || 0), 0);
    const totalPP = itemsPP + (parseFloat(formData.transportationTotalPp) || 0) + (parseFloat(formData.customizationTotalPp) || 0) + (parseFloat(formData.marketingTotalPp) || 0);
    const totalSP = itemsSP + (parseFloat(formData.transportationTotalSp) || 0) + (parseFloat(formData.customizationTotalSp) || 0) + (parseFloat(formData.marketingTotalSp) || 0);
    const margin  = totalSP - totalPP;
    const pct     = totalPP !== 0 ? (margin / totalPP) * 100 : 0;
    setFormData(prev => ({
      ...prev, createdBy: username, userEmail: email,
      totalPP: totalPP.toFixed(2), totalSP: totalSP.toFixed(2),
      totalMargin: margin.toFixed(2), totalMarginPercent: pct.toFixed(2),
    }));
  }, [
    formData.items,
    formData.transportationTotalPp, formData.transportationTotalSp,
    formData.customizationTotalPp,  formData.customizationTotalSp,
    formData.marketingTotalPp,      formData.marketingTotalSp,
    username, email,
  ]);

  // ── AUTH ──────────────────────────────────────────────────────────────────
  const sendAuthenticatedRequest = async (url, method, body) => {
    const token = await getApiToken(instance, account);
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) throw new Error(`API ${res.status}`);
    return res.headers.get('content-type')?.includes('application/json') ? res.json() : res.text();
  };

  // ── CATALOG DATA ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!account) return;
    (async () => {
      setCatalogLoading(true);
      try {
        const res = await sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/smart-search/filters`, 'GET');
        if (res) {
          setBrands(res.brands.map(b => ({ id: b, name: b })));
          setCategories(res.categories.map(c => ({ id: c, name: c })));
          const map = {};
          for (const cat of res.categories) {
            const p   = new URLSearchParams({ category: cat });
            const sub = await sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/smart-search/filters?${p}`, 'GET');
            if (sub?.subcategories) map[cat] = sub.subcategories.map(s => ({ id: s, name: s }));
          }
          setCategorySubcategoryMap(map);
        }
      } catch (e) { console.error(e); }
      finally { setCatalogLoading(false); }
    })();
  }, [account]);

  // ── LEGAL CLIENTS ─────────────────────────────────────────────────────────
    useEffect(() => {
        refreshLegalClientsAndVendors();
    }, [refreshLegalClientsAndVendors]);  


  // ── SALES ORDER CHECK ─────────────────────────────────────────────────────
  const checkSalesOrder = async (raw = '') => {
    if(isAutoSo) return ;
    const val = raw.trim();
    if (!val) { setSalesOrderExists(false); setSalesOrderError(''); return; }
    if (!/^[A-Z0-9/-]+$/i.test(val) || /\s/.test(raw)) {
      setSalesOrderExists(true);
      setSalesOrderError('Only letters, numbers, "/" and "-". No spaces.');
      return;
    }
    try {
      const data = await (await fetch(`${urlconfig.backend_api_base_url}/api/v2/check-sales-order/${encodeURIComponent(val.replaceAll('/', '_SLASH_'))}`)).json();
      setSalesOrderExists(data.exists);
      setSalesOrderError(data.exists ? 'Sales Order Number already exists.' : '');
    } catch { setSalesOrderExists(false); }
  };

  // ── FORM HANDLERS ─────────────────────────────────────────────────────────
  const handleFormChange = (field, value) => {
    //When switching to Internal
  if (field === 'costsheetfor' && value === 'internal') {
    setFormData(prev => ({
      ...prev,
      costsheetfor: value,
      clientName: 'Internal',
      //reseting field, when user select Internal
      items: prev.items.map(() => ({
        ...INITIAL_ITEM
      })),
    }));
    return;
  }

  // When switching back from Internal
  if (field === 'costsheetfor' && value !== 'internal') {
    setFormData(prev => ({
      ...prev,
      costsheetfor: value,
      clientName: '',
      ...(prev.costsheetfor === 'internal' ? { items: prev.items.map(() => ({ ...INITIAL_ITEM })) } : {}),
    }));
    return;
  }
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      // if (field === 'costsheetfor' && value === 'Sample') { next.salesOrderNo = ''; setSalesOrderExists(false); setSalesOrderError(''); }      
      if (field === 'costsheetfor') {
        next.salesOrderNo = '';

        setSalesOrderExists(false);
        setSalesOrderError('');
      }
      return next;
    });
    if (field === 'clientPaymentTerms') { setShowCustomClientTerm(value === 'custom'); if (value !== 'custom') setCustomClientTerm(''); }
    if (field === 'salesOrderNo') checkSalesOrder(value);
  };

  const recalc = (item) => {
    const pp = parseFloat(item.pp) || 0, sp = parseFloat(item.sp) || 0;
    const gst = parseFloat(item.gst) || 0, qty = parseInt(item.qty) || 0;
    const nlc = pp * (1 + gst / 100), nsp = sp * (1 + gst / 100);
    const tPP = pp * qty, tSP = sp * qty, mg = tSP - tPP;
    const tPPGST=  nlc * qty, tSPGST = nsp *qty;
    return {
      ...item, nlc: nlc.toFixed(2), nsp: nsp.toFixed(2),
      totalPp: tPP.toFixed(2), totalSp: tSP.toFixed(2), margin: mg.toFixed(2), totPPGST: tPPGST.toFixed(2),totSPGST: tSPGST.toFixed(2),
      marginPercent: tPP !== 0 ? ((mg / tPP) * 100).toFixed(1) : '0.0',
    };
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, idx) => {
        if (idx !== index) return item;
        const next = { ...item, [field]: value };
        if (field === 'category') next.subcategory = null;
        if (field === 'vendorPaymentTerms' && value !== 'custom')
          setCustomVendorTerms(p => { const n = { ...p }; delete n[index]; return n; });
        return recalc(next);
      }),
    }));
  };

  const addItem    = () => setFormData(prev => ({ ...prev, items: [...prev.items, { ...INITIAL_ITEM }] }));
  const removeItem = (i) => {
    setFormData(prev => ({ ...prev, items: prev.items.filter((_, idx) => idx !== i) }));
    setCustomVendorTerms(prev => { const n = { ...prev }; delete n[i]; return n; });
  };

  // ── CATALOG SELECT ────────────────────────────────────────────────────────
  const handleCatalogSelect = async (product) => {
    const findIn = (list, name) => name ? list.find(i => i.name.toLowerCase() === name.toLowerCase()) || null : null;
    const ensure = async (type, name, parentId = null) => {
      if (!name) return null;
      try { return await sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/catalog/ensure-${type}`, 'POST', parentId ? { name, categoryId: parentId } : { name }); }
      catch { return { name }; }
    };

    let tBrand = product.brandName ? findIn(brands, product.brandName) : null;
    if (product.brandName && !tBrand) { tBrand = await ensure('brand', product.brandName); if (tBrand) setBrands(p => [...p, tBrand]); }

    let tCat = findIn(categories, product.omsCategory);
    if (!tCat && product.omsCategory) { tCat = await ensure('category', product.omsCategory); if (tCat) { setCategories(p => [...p, tCat]); setCategorySubcategoryMap(p => ({ ...p, [tCat.id || tCat.name]: [] })); } }

    let tSub = null;
    if (tCat && product.omsSubcategory) {
      const key = tCat.id || tCat.name;
      tSub = findIn(categorySubcategoryMap[key] || [], product.omsSubcategory);
      if (!tSub) { tSub = await ensure('subcategory', product.omsSubcategory, tCat.id); if (tSub) setCategorySubcategoryMap(p => ({ ...p, [key]: [...(p[key] || []), tSub] })); }
    }

    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, idx) => {
        if (idx !== targetRowIndex) return item;
        const u = { ...item, details: product.productName || '', ecomProductName: product.productName || '', esin: product.ecomProductId?.toString() || '' };
        if (tBrand) u.brand = tBrand;
        if (tCat)  { u.category = tCat; u.subcategory = tSub; }
        if (product.ecomMrp)           u.sp = product.ecomMrp.toString();
        if (product.lastPurchasePrice) u.pp = product.lastPurchasePrice.toString();
        if (product.lastVendor)        u.vendorName = product.lastVendor;
        return recalc(u);
      }),
    }));
    setProductModalOpen(false);
  };

  // ── SUBMIT ────────────────────────────────────────────────────────────────
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!e.currentTarget.checkValidity()) { setValidated(true); return; }
    setValidated(false); setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const payTerm = formData.clientPaymentTerms === 'custom' ? customClientTerm : formData.clientPaymentTerms;
      const payload = {
        ...formData,
        clientName: isInternal ? 'Internal' : (selectedLegalClient?.legalClientName || formData.clientName),
        legalClientId: selectedLegalClient?.id || null,
        clientPaymentTerms: payTerm,
        costSheetType: isSample ? 'Sample' : 'Order',
        salesOrderNo:  isAutoSo ? null : formData.salesOrderNo,
        sampleStatus:  isSample ? formData.sampleStatus : null,

        costSummary: {
          transportationGST: parseFloat(formData.transportationGST) || 0,
          customizationGST:  parseFloat(formData.customizationGST)  || 0,
          marketingGST:      parseFloat(formData.marketingGST)      || 0,
        },

         customRows: (customRows || []).map(row => ({
          customLabel: row?.customLabel || '',
          customGST: Number(row?.customGST) || 0,
          customTotalPp: Number(row?.customTotalPp) || 0,
          customTotalSp: Number(row?.customTotalSp) || 0,
        })),
        items: formData.items.map((item, i) => ({
          ...item,
          legalVendorId: item.legalVendorId === 'custom' ? null : item.legalVendorId,
          isCustomVendor: item.legalVendorId === 'custom',
          vendorPaymentTerms: item.vendorPaymentTerms === 'custom' ? customVendorTerms[i] || '' : item.vendorPaymentTerms,
          gst: parseFloat(item.gst) || 0, pp: parseFloat(item.pp) || 0, sp: parseFloat(item.sp) || 0,
          marginPercent: parseFloat(item.marginPercent) || 0,
          brand:       item.brand       ? { name: item.brand.name }       : null,
          category:    item.category    ? { name: item.category.name }    : null,
          subcategory: item.subcategory ? { name: item.subcategory.name } : null,
        })),
        totalPP: parseFloat(formData.totalPP) || 0, totalSP: parseFloat(formData.totalSP) || 0,
        totalMargin: parseFloat(formData.totalMargin) || 0, totalMarginPercent: parseFloat(formData.totalMarginPercent) || 0,
        transportationTotalPp: parseFloat(formData.transportationTotalPp) || 0, transportationTotalSp: parseFloat(formData.transportationTotalSp) || 0,
        customizationTotalPp:  parseFloat(formData.customizationTotalPp)  || 0, customizationTotalSp:  parseFloat(formData.customizationTotalSp)  || 0,
        marketingTotalPp:      parseFloat(formData.marketingTotalPp)      || 0, marketingTotalSp:      parseFloat(formData.marketingTotalSp)      || 0,
      };
      await sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/update-master-csv`, 'POST', payload);
      await sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/send-email`, 'POST', { salesOrderNo: formData.salesOrderNo, createdBy: username, userEmail: email });
      clearForm(); setShowConfirm(false);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const clearForm = () => {
    setFormData({ date: '', deliveryDate: '', createdBy: '', userEmail: '', clientName: '', clientPaymentTerms: '', costsheetfor: '', salesOrderNo: '', salesPOC: '', scmspoc: '', costSheetRemarks: '', orderStatus: 'Pending', items: [{ ...INITIAL_ITEM }], transportationTotalPp: '0', transportationTotalSp: '0', customizationTotalPp: '0', customizationTotalSp: '0', marketingTotalPp: '0', marketingTotalSp: '0', totalPP: '0.00', totalSP: '0.00', totalMargin: '0.00', totalMarginPercent: '0.00', location: '', sampleStatus: 'Yet to receive from Vendor' , transportationGST:'0', customizationGST:'0', marketingGST:'0'});
    setCustomClientTerm(''); setShowCustomClientTerm(false); setCustomVendorTerms({}); setSalesOrderExists(false); setSalesOrderError(''); setValidated(false);
    setCustomRows([]);
    setSelectedLegalClient(null);
  };

  // ── DERIVED ───────────────────────────────────────────────────────────────
  const totalPP   = parseFloat(formData.totalPP) || 0;
  const totalSP   = parseFloat(formData.totalSP) || 0;
  const margin    = totalSP - totalPP;
  const marginPct = totalPP !== 0 ? ((margin / totalPP) * 100).toFixed(1) : '0.0';

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <PageShell>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Mono:wght@400;500&display=swap');`}</style>

      {/* PAGE HEADER */}
      <PageHeader>
        <AssignmentIcon sx={{ fontSize: 30 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={800} letterSpacing="-0.5px" fontFamily={FONT.display}>
            New Cost Sheet
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.65 }}>Fill in all sections and submit for review</Typography>
        </Box>
        {totalPP > 0 && (
          <AggregateBar stats={[
            { label: 'Total Cost',    value: `₹${formatCurrency(totalPP)}`, variant: 'cost'    },
            { label: 'Total Revenue', value: `₹${formatCurrency(totalSP)}`, variant: 'revenue' },
            { label: 'Margin',        value: `${marginPct}%`,               variant: 'profit'  },
          ]} />
        )}
      </PageHeader>

      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ px: { xs: 2, md: 4 }, py: 3 }}>

        {/* ── SECTION 1 ── */}
        <SectionCard>
          <SectionHeader accent={COLORS.indigo}>
            <BusinessCenterIcon sx={{ fontSize: 16 }} />
            Order Information
          </SectionHeader>
          <SectionBody>
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small" required error={validated && !formData.costsheetfor} sx={FIELD_SX}>
                  <InputLabel>Cost Sheet For</InputLabel>
                  <Select value={formData.costsheetfor} label="Cost Sheet For"
                    onChange={(e) => handleFormChange('costsheetfor', e.target.value)} sx={SELECT_SX}>
                    <MenuItem value="" disabled>Select type…</MenuItem>
                    <MenuItem value="eyantraventures">Eyantra Ventures</MenuItem>
                    <MenuItem value="Mozato">Mozato</MenuItem>
                    <MenuItem value="eYantraIndustries">eYantra Industries</MenuItem>
                    <MenuItem value="internal">Internal&nbsp;<Chip label="No SO" size="small" sx={{ height: 16, fontSize: 10, bgcolor: alpha(COLORS.amber, 0.18), color: COLORS.amber, fontWeight: 700 }}/></MenuItem>
                    <MenuItem value="inventory">Inventory&nbsp;<Chip label="No SO" size="small" sx={{ height: 16, fontSize: 10, bgcolor: alpha(COLORS.amber, 0.18), color: COLORS.amber, fontWeight: 700 }}/></MenuItem>
                    <MenuItem value="Sample">
                      Sample&nbsp;
                      <Chip label="No SO" size="small" sx={{ height: 16, fontSize: 10, bgcolor: alpha(COLORS.amber, 0.18), color: COLORS.amber, fontWeight: 700 }} />
                    </MenuItem>
                  </Select>
                  {validated && !formData.costsheetfor && <Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block' }}>Required</Typography>}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth size="small" label="Created By" value={username} disabled sx={FIELD_SX} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth size="small" label="Email" value={email} disabled sx={FIELD_SX} />
              </Grid>
            </Grid>
          </SectionBody>
        </SectionCard>

        {/* ── SECTION 2 ── */}
        <SectionCard>
          <SectionHeader accent={COLORS.navy}>
            <CalendarMonthIcon sx={{ fontSize: 16 }} />
            Client & Schedule
          </SectionHeader>
          <SectionBody>
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={3}>
                <TextField fullWidth size="small" type="date" label={isSample ? 'Sample Date' : 'SO Date'} value={formData.date}
                  onChange={(e) => handleFormChange('date', e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{max: new Date().toISOString().split("T")[0],}}
                  required error={validated && !formData.date} helperText={validated && !formData.date ? 'Required' : ''} sx={FIELD_SX} />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField fullWidth size="small" type="date" label="Delivery Date" value={formData.deliveryDate}
                  onChange={(e) => handleFormChange('deliveryDate', e.target.value)} InputLabelProps={{ shrink: true }}
                  required error={validated && !formData.deliveryDate} helperText={validated && !formData.deliveryDate ? 'Required' : ''} sx={FIELD_SX} />
              </Grid>
              {!isInternal && (
              <Grid item xs={12} md={3}>
                <Autocomplete
                  options={legalClients}
                  getOptionLabel={(o) => o.legalClientName || ''}
                  value={selectedLegalClient}
                  onChange={(_, newVal) => setSelectedLegalClient(newVal)}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', py: 1 }}>
                      <Typography variant="body2" fontWeight={600} fontSize={13}>
                        {option.legalClientName}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1.5, mt: 0.25 }}>
                        {option.clientGST && (
                          <Typography variant="caption" sx={{ color: COLORS.textMuted, fontFamily: FONT.mono, fontSize: 11 }}>
                            GST: {option.clientGST}
                          </Typography>
                        )}
                        {option.clientState && (
                          <Typography variant="caption" sx={{ color: COLORS.textMuted, fontSize: 11 }}>
                            {option.clientState}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Legal Client Name"
                      required
                      error={validated && !selectedLegalClient}
                      helperText={validated && !selectedLegalClient ? 'Required' : ''}
                      sx={FIELD_SX}
                    />
                  )}
                />
              </Grid>
              )}
              <Grid item xs={12} md={3}>
                <TextField fullWidth size="small" label="Delivery Location" value={formData.location}
                  onChange={(e) => handleFormChange('location', e.target.value)}
                  required error={validated && !formData.location} helperText={validated && !formData.location ? 'Required' : ''} sx={FIELD_SX} />
              </Grid>
              <Grid item xs={12} md={3}>
                <UserSelect label="Sales POC" value={formData.salesPOC} onChange={(v) => handleFormChange('salesPOC', v)}
                  required error={validated && !formData.salesPOC} helperText={validated && !formData.salesPOC ? 'Required' : ''} />
              </Grid>
              <Grid item xs={12} md={3}>
                <UserSelect label="SCM SPOC" value={formData.scmspoc} onChange={(v) => handleFormChange('scmspoc', v)}
                  required error={validated && !formData.scmspoc} helperText={validated && !formData.scmspoc ? 'Required' : ''} />
              </Grid>
              {!isInternal &&(
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small" required error={validated && !formData.clientPaymentTerms} sx={FIELD_SX}>
                  <InputLabel>Client Payment Terms</InputLabel>
                  <Select value={formData.clientPaymentTerms} label="Client Payment Terms"
                    onChange={(e) => handleFormChange('clientPaymentTerms', e.target.value)} sx={SELECT_SX}>
                    {PAYMENT_TERM_OPTIONS.map(({ value, label }) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                  </Select>
                </FormControl>
                {showCustomClientTerm && (
                  <TextField fullWidth size="small" sx={{ mt: 1, ...FIELD_SX }} placeholder="e.g. 90 days net"
                    value={customClientTerm} onChange={(e) => setCustomClientTerm(e.target.value)} required />
                )}
              </Grid>
              )}
              <Grid item xs={12} md={3}>
                <TextField fullWidth size="small"
                  label={isSample ? 'Sample ID' : isInternal ? 'Sales Order No.' : 'Sales Order No'}
                  value={isAutoSo ? '(auto-generated)' : formData.salesOrderNo}
                  onChange={(e) => handleFormChange('salesOrderNo', e.target.value)}
                  onBlur={() => checkSalesOrder(formData.salesOrderNo)}
                  required={!isAutoSo} disabled={isAutoSo}
                  error={(validated && !isSample && !formData.salesOrderNo) || salesOrderExists}
                  helperText={salesOrderExists ? salesOrderError : (validated && !isSample && !formData.salesOrderNo ? 'Required' : '')}
                  sx={FIELD_SX} />
              </Grid>
              {isInternal && (
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small" required error={validated && !formData.internalCategory} sx={FIELD_SX}>
                  <InputLabel>Internal Category</InputLabel>
                  <Select value={formData.internalCategory} label="Internal Category"
                    onChange={(e) => handleFormChange('internalCategory', e.target.value)} sx={SELECT_SX}>
                    {INTERNAL_CATEGORY.map(({ value, label }) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {/* Legal Client Lookup */}
            
              {/* Legal Client Info Box */}
              {selectedLegalClient && (
                <Grid item xs={12}>
                  <Box sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(COLORS.indigo, 0.03),
                    border: `1px solid ${alpha(COLORS.indigo, 0.15)}`,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <BusinessCenterIcon sx={{ fontSize: 14, color: COLORS.indigo }} />
                      <Typography variant="caption" sx={{
                        fontWeight: 700, textTransform: 'uppercase',
                        letterSpacing: '0.07em', color: COLORS.indigo,
                      }}>
                        Legal Client Details
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      <Chip
                        label="Verified"
                        size="small"
                        sx={{
                          height: 18, fontSize: 10, fontWeight: 700,
                          bgcolor: alpha(COLORS.success, 0.12),
                          color: COLORS.success,
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                      {[
                        { label: 'Legal Name', value: selectedLegalClient.legalClientName, flex: 1 },
                        { label: 'GST', value: selectedLegalClient.clientGST, flex: 1 },
                        { label: 'State', value: selectedLegalClient.clientState, flex: 1 },
                        { label: 'Address', value: selectedLegalClient.clientAddress, flex: 1 },
                        { label: 'Contact Person', value: selectedLegalClient.clientSpoc, flex: 1 },
                        { label: 'Mobile', value: selectedLegalClient.clientSpocNumber, flex: 1 },
                        {label: 'Sales Poc', value: selectedLegalClient.cSalesPoc, flex :1},
                      ].map(({ label, value, flex }) => !value ? null : (
                        <Box key={label} sx={{
                          flex, minWidth: 120, px: 1.5, py: 1,
                          borderRight: `1px solid ${alpha(COLORS.indigo, 0.1)}`,
                          '&:last-of-type': { borderRight: 'none' },
                        }}>
                          <Typography variant="caption" sx={{
                            fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                            letterSpacing: '0.06em', color: COLORS.textMuted, display: 'block', mb: 0.25,
                          }}>
                            {label}
                          </Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ color: COLORS.textPrimary, fontSize: 13 }}>
                            {value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Grid>
              )}

            </Grid>

            {isSample && (
              <SampleBox>
                <Typography variant="caption" sx={{ color: COLORS.amber, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 1.5 }}>
                  Sample Details
                </Typography>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small" required sx={FIELD_SX}>
                      <InputLabel>Sample Status</InputLabel>
                      <Select value={formData.sampleStatus} label="Sample Status"
                        onChange={(e) => handleFormChange('sampleStatus', e.target.value)} sx={SELECT_SX}>
                        <MenuItem value="Yet to receive from Vendor">Yet to receive from Vendor</MenuItem>
                        <MenuItem value="At eyantra Location">At eYantra Location</MenuItem>
                        <MenuItem value="With Client">With Client</MenuItem>
                        <MenuItem value="Sent back to vendor">Sent back to Vendor</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField fullWidth size="small" multiline rows={1} label="Remarks"
                      value={formData.costSheetRemarks}
                      onChange={(e) => handleFormChange('costSheetRemarks', e.target.value)}
                      sx={FIELD_SX} />
                  </Grid>
                </Grid>
              </SampleBox>
            )}
          </SectionBody>
        </SectionCard>

        {/* ── SECTION 3 ── */}
        <SectionCard>
          <SectionHeader accent={COLORS.success}
            sx={{ cursor: 'pointer', userSelect: 'none' }}
            onClick={() => setItemsExpanded(e => !e)}>
            <InventoryIcon sx={{ fontSize: 16 }} />
            <Box sx={{ flex: 1 }}>
              Line Items
              <Chip label={`${formData.items.length} item${formData.items.length !== 1 ? 's' : ''}`} size="small"
                sx={{ ml: 1.5, height: 18, fontSize: 10, bgcolor: alpha(COLORS.success, 0.12), color: COLORS.success, fontWeight: 700 }} />
            </Box>
            <ExpandMoreIcon sx={{ fontSize: 18, color: COLORS.textMuted, transition: 'transform 0.2s', transform: itemsExpanded ? 'rotate(180deg)' : 'none' }} />
          </SectionHeader>

          {itemsExpanded && (
            <LineItemsTable
              items={formData.items}
              onItemChange={handleItemChange}
              onAddItem={addItem}
              isInternal={formData.costsheetfor === 'internal'}
              onRemoveItem={removeItem}
              subtotals={{
                transportationGST: formData.transportationGST, 
                transportationTotalPp: formData.transportationTotalPp, transportationTotalSp: formData.transportationTotalSp,
                customizationGST:  formData.customizationGST,
                customizationTotalPp:  formData.customizationTotalPp,  customizationTotalSp:  formData.customizationTotalSp,
                marketingGST: formData.marketingGST,
                marketingTotalPp:      formData.marketingTotalPp,       marketingTotalSp:      formData.marketingTotalSp,
              }}
              onSubtotalChange={(field, value) => setFormData(prev => ({ ...prev, [field]: value }))}
              brands={brands} categories={categories} categorySubcategoryMap={categorySubcategoryMap}
              catalogLoading={catalogLoading}
              customVendorTerms={customVendorTerms}
              onCustomVendorTermChange={(i, v) => setCustomVendorTerms(p => ({ ...p, [i]: v }))}
              onOpenCatalog={(i) => { setTargetRowIndex(i); setProductModalOpen(true); }}
              qtyErrors={qtyErrors}
              onQtyErrorChange={(i, err) => setQtyErrors(p => ({ ...p, [i]: err }))}
              validated={validated}
              customRows={customRows}
              onCustomRowChange={setCustomRows}
              legalVendors={legalVendors}
              onLegalVendorChange={(i, vendorObj) => handleItemChange(i, 'legalVendorId', vendorObj?.id || null)}
            />
          )}
        </SectionCard>

        {/* SUBMIT BAR */}
        <SubmitBar>
          <Button variant="text" startIcon={<ClearAllIcon />} onClick={clearForm}
            sx={{ textTransform: 'none', fontWeight: 600, color: COLORS.textMuted, borderRadius: 2 }}>
            Clear Form
          </Button>
          <Button type="submit" variant="contained" disabled={salesOrderExists && !isSample}
            startIcon={<CheckCircleOutlineIcon />}
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 3,
              background: `linear-gradient(135deg, ${COLORS.indigo}, ${COLORS.navy})`,
              boxShadow: '0 4px 14px rgba(99,102,241,0.35)',
              '&:hover': { background: `linear-gradient(135deg, ${COLORS.navy}, ${COLORS.indigo})` },
              '&:disabled': { opacity: 0.5 },
            }}>
            Submit Cost Sheet
          </Button>
        </SubmitBar>
      </Box>

      {/* CONFIRM MODAL */}
      <Modal open={showConfirm} onClose={() => !loading && setShowConfirm(false)}>
        <ConfirmPaper>
          <Box sx={{ p: '18px 24px', background: COLORS.navy, color: COLORS.white }}>
            <Typography variant="h6" fontWeight={800} fontFamily={FONT.display}>Confirm Submission</Typography>
            <Typography variant="caption" sx={{ opacity: 0.65 }}>Review before finalising</Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              {[
                { label: 'Client', value: isInternal ? 'Internal' : selectedLegalClient?.legalClientName },
                { label: 'Type',          value: formData.costsheetfor },
                { label: 'SO No.',        value: isAutoSo ? '(auto-generated)' : formData.salesOrderNo },
                { label: 'Line Items',    value: formData.items.length },
                { label: 'Total Cost',    value: `₹${formatCurrency(totalPP)}` },
                { label: 'Total Revenue', value: `₹${formatCurrency(totalSP)}` },
              ].map(({ label, value }) => (
                <Grid item xs={6} key={label}><SummaryKv label={label} value={value} /></Grid>
              ))}
            </Grid>

            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(COLORS.success, 0.06), border: `1px solid ${alpha(COLORS.success, 0.2)}`, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUpIcon sx={{ fontSize: 16, color: COLORS.success }} />
              <Typography variant="body2" fontWeight={700} color={COLORS.success}>
                Margin: ₹{formatCurrency(margin)} ({marginPct}%)
              </Typography>
            </Box>

            {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1, bgcolor: alpha(COLORS.indigo, 0.12), '& .MuiLinearProgress-bar': { bgcolor: COLORS.indigo } }} />}

            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={() => setShowConfirm(false)} disabled={loading}
                sx={{ borderRadius: 2, textTransform: 'none', borderColor: COLORS.border, color: COLORS.textMuted }}>Cancel</Button>
              <Button variant="contained" onClick={handleConfirm} disabled={loading}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 3, background: `linear-gradient(135deg, ${COLORS.indigo}, ${COLORS.navy})` }}>
                {loading ? 'Submitting…' : 'Confirm & Submit'}
              </Button>
            </Box>
          </Box>
        </ConfirmPaper>
      </Modal>

      {/* CATALOG MODAL */}
      <ProductSelectorModal
        open={productModalOpen}
        onClose={() => setProductModalOpen(false)}
        onSelect={handleCatalogSelect}
        fetchDataFunc={sendAuthenticatedRequest}
        baseUrl={urlconfig.backend_api_base_url}
        brandsOptions={brands}
        categoriesOptions={categories}
        categorySubcategoryMap={categorySubcategoryMap}
      />
    </PageShell>
  );
};

export default UserInput;